import * as Bixby from '$/core';
import { LoggerService } from '$/services/LoggerService';
import { store } from '$/store';
import { msalLoginFailure, msalLoginSuccess, resumeFromMsalCache } from '$/store/msal/actions';
import {
  AccountInfo,
  AuthenticationResult,
  Configuration,
  EndSessionRequest,
  InteractionRequiredAuthError,
  LogLevel,
  PublicClientApplication,
  RedirectRequest,
  SilentRequest,
} from '@azure/msal-browser';

const { MsalAppEvents, CscpEvents } = Bixby.Constants;
type IframeMsalPayload = Bixby.Models.IframeMsalPayload;
type TokenAsyncRequest = Bixby.Models.TokenAsyncRequest;

type CscpToBixbyAuthPayload = Bixby.Models.CscpToBixbyAuthPayload;
type IframeTokenRequestPayload = Bixby.Models.CscpToBixbyTokenPayload;

type CscpMsalDto = Bixby.Models.CustomIframeMessage<CscpToBixbyAuthPayload | IframeTokenRequestPayload>;

export abstract class MsalService {
  private static myMSALObj: PublicClientApplication | null;
  private static account: AccountInfo;
  private static loginScopes: Array<string> = [];
  private static plannerScopes: Array<string> = ['Group.ReadWrite.All'];
  private static silentPlannerScopes: Array<string> = ['openid', 'profile', 'Group.ReadWrite.All'];
  private static peopleScopes: Array<string> = ['Directory.Read.All'];
  private static silentPeopleScopes: Array<string> = ['openid', 'profile', 'Directory.Read.All'];

  /** Handle asyncrony between iframe and parent for token services to still return promises to external code*/
  private static pendingTokenRequests = new Map<string, TokenAsyncRequest>();
  /** Used to identify token request since resolve functions can't be transported across the parent window and iframe barrier. */
  private static id = 0;
  /** Console error triggered if access tokens take too long to generate */
  private static PARENT_WINDOW_MSAL_TOKEN_TIMEOUT = 30000;
  /** When embedded within CreditSights we must instantiate MSAL with the same redirect URI. */
  private static getCscpHostUri = () => {
    const protocol = 'https://';
    const cscpUriIncludesProtocol =
      Bixby.Configuration.cscpUri.startsWith('http://') || Bixby.Configuration.cscpUri.startsWith('https://');
    // Handle local dev scenarios where CSCP is running on http
    if (cscpUriIncludesProtocol) {
      return `${Bixby.Configuration.cscpUri}${Bixby.Configuration.cscpWindowPathPrefix}`;
    }
    return `${protocol}${Bixby.Configuration.cscpUri}${Bixby.Configuration.cscpWindowPathPrefix}`;
  };

  /**
   * Configuration class for @azure/msal-browser:
   * https://azuread.github.io/microsoft-authentication-library-for-js/ref/msal-browser/modules/_src_config_configuration_.html
   */
  private static getMsalConfig = (): Configuration => ({
    auth: {
      clientId: Bixby.Configuration.msalClientId,
      authority: Bixby.Configuration.msalAuthority,
      redirectUri: Bixby.Configuration.isStandAloneMode ? Bixby.Configuration.msalRedirectUri : this.getCscpHostUri(),
    },
    cache: {
      cacheLocation: 'localStorage', // This configures where your cache will be stored
      storeAuthStateInCookie: false, // Set this to "true" if you are having issues on IE11 or Edge
    },
    system: {
      loggerOptions: {
        loggerCallback: (level, message, containsPii) => {
          if (containsPii) {
            return;
          }
          switch (level) {
            case LogLevel.Error:
              // eslint-disable-next-line no-console
              console.error(message);
              return;
            case LogLevel.Info:
              // eslint-disable-next-line no-console
              console.info(message);
              return;
            case LogLevel.Verbose:
              // eslint-disable-next-line no-console
              console.debug(message);
              return;
            case LogLevel.Warning:
              // eslint-disable-next-line no-console
              console.warn(message);
              return;
          }
        },
      },
    },
  });

  //#region Public API
  public static getAccount(): AccountInfo {
    return MsalService.account;
  }

  public static resumeFromCache(): boolean {
    const account = MsalService.determineAccount();
    if (account !== null) {
      MsalService.account = account;
      return true;
    }
    return false;
  }
  public static init() {
    this.myMSALObj = new PublicClientApplication(this.getMsalConfig());
    if (Bixby.Configuration.isStandAloneMode) {
      // Load auth module when browser window loads. Only required for redirect flows.
      // debugger
      // If window loaded invoke load auth module
      if(document.readyState === 'complete'){
        LoggerService.log(`Window already loaded, invoking loadAuthModule directly.`);
        MsalService.loadAuthModule();
        return;
      }else{
        LoggerService.log(`Setting up window load event listener to invoke loadAuthModule. Current window state ${document.readyState}`);
        window.addEventListener('load', async () => {
          MsalService.loadAuthModule();
        });
      }
    } else {
      // When bixby is in an iframe we need to subscribe from posted messages for auth session and tokens
      LoggerService.log(`Setting up event listener for window message.`);
      window.addEventListener('message', msg => {
        MsalService.handleMessageForMsal(msg);
      });
    }
  }
  /** Flag to indicate that we have account and ability to get tokens so no need to track inital page requests anymore */
  public static setUpComplete = false;

  /**
   * Calls loginRedirect.
   * @param signInType
   */
  public static async loginAsync(): Promise<void> {
    const payload = {
      scopes: MsalService.loginScopes,
      redirectStartPage: window.location.href,
    };
    await MsalService.myMSALObj?.loginRedirect(payload);
  }

  /** Uses window post to get parent of iframe window to initiate msal session. */
  public static async msalInIframeLogin(): Promise<void> {
    return this.postRequestToParentOfIframe(
      MsalAppEvents.MsalAppTriggerAuth,
      this.getMsalInIframeRequest([], undefined)
    );
  }

  /**
   * Checks whether we are in the middle of a redirect and handles state accordingly. Only required for redirect flows.
   *
   * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/initialization.md#redirect-apis
   */
  public static async loadAuthModule(): Promise<void> {
    const response: AuthenticationResult | null = (await MsalService.myMSALObj?.handleRedirectPromise()) ?? null;
    MsalService.handleResponse(response);
    if (MsalService.account !== null) {
      store.dispatch(resumeFromMsalCache() as any);
    }
  }

  /**
   * Logs out of current account.
   */
  public static logout(): void {
    const logOutRequest: EndSessionRequest = {
      account: MsalService.account,
    };
    MsalService.myMSALObj?.logout(logOutRequest);
  }

  public static async getPlannerToken(): Promise<string | null> {
    if (Bixby.Configuration.isStandAloneMode) {
      return this.getPlannerTokenRedirectStandAlone();
    }
    return this.getPlannerSearchTokenFromIframe();
  }

  public static async getPeopleSearchToken(): Promise<string | null> {
    if (Bixby.Configuration.isStandAloneMode) {
      return this.getPeopleSearchTokenRedirectStandAlone();
    }
    return this.getPeopleSearchTokenFromIframe();
  }
  /**
   * Handles receiving an auth response from the parent window via message event.
   * @param msg
   * */
  public static handleMessageForMsal = (msg: MessageEvent<CscpMsalDto>): void => {
    const { event, payload } = msg.data ?? {};
    if (event === CscpEvents.CscpMsalTokenReceived) {
      LoggerService.log('handleMessageForMsal: CscpMsalTokenReceived', payload);
      const { pendingTokenRequestId, token } = payload as IframeTokenRequestPayload;
      const pendingRequest = this.pendingTokenRequests.get(pendingTokenRequestId);
      clearTimeout(pendingRequest?.timer);
      pendingRequest?.resolve(token);
    } else if (event === CscpEvents.CscpLoginRedirectSuccess) {
      LoggerService.log('handleMessageForMsal: CscpLoginRedirectSuccess', payload);
      const { authRes, account } = payload as CscpToBixbyAuthPayload;
      // Set account from parent window as we'll need that to construct the silent token requests
      if (authRes?.account) {
        MsalService.account = authRes.account;
      } else if (account) {
        MsalService.account = account;
      } else {
        LoggerService.log(`msalLoginFailure`);
        store.dispatch(msalLoginFailure(new Error('No account available after login redirect success.')));
        return;
      }
      LoggerService.log(`Dispatching msal login success!`);
      this.setUpComplete = true;
      store.dispatch(msalLoginSuccess());
    }
  };

  //#endregion
  //#region Private
  /**
   * Gets the token to submit tasks to planner silently, or falls back to interactive redirect.
   */
  private static async getPlannerTokenRedirectStandAlone(): Promise<string | null> {
    // https://azuread.github.io/microsoft-authentication-library-for-js/ref/msal-browser/modules/_src_request_silentrequest_.html
    return MsalService.getTokenRedirect(this.getSilentPlannerRequest(), {
      scopes: MsalService.plannerScopes,
      redirectStartPage: globalThis.location.href,
    });
  }

  /**
   * Gets the token to search people silently, or falls back to interactive redirect.
   */
  private static async getPeopleSearchTokenRedirectStandAlone(): Promise<string | null> {
    // https://azuread.github.io/microsoft-authentication-library-for-js/ref/msal-browser/modules/_src_request_silentrequest_.html
    return MsalService.getTokenRedirect(this.getSilentPeopleRequest(), {
      scopes: MsalService.peopleScopes,
      redirectStartPage: globalThis.location.href,
    });
  }

  /** Reused between in iframe and standalone msal requests. */
  private static readonly getSilentPlannerRequest = (): SilentRequest => ({
    scopes: MsalService.silentPlannerScopes,
    account: MsalService.account,
    forceRefresh: false,
  });

  /** Reused between in iframe and standalone msal requests. */
  private static readonly getSilentPeopleRequest = (): SilentRequest => ({
    scopes: MsalService.silentPeopleScopes,
    account: MsalService.account,
    forceRefresh: false,
  });

  /** Creates timestamped token request id with incremental counter for tracking tokens we need from the parent window. */
  private static getNextTokenReqId() {
    return `token-req_${Date.now()}_${this.id}`;
  }

  /**
   * Gets the token to search people from the parent window when Bixby is in an iframe
   */
  private static getPeopleSearchTokenFromIframe(): Promise<string | null> {
    const message = this.getMsalInIframeRequest(MsalService.silentPeopleScopes, this.getSilentPeopleRequest());
    return this.getTokenPromise(message);
  }
  /**
   * Gets the token to search planner from the parent window when Bixby is in an iframe
   */
  private static getPlannerSearchTokenFromIframe(): Promise<string | null> {
    const message = this.getMsalInIframeRequest(MsalService.silentPlannerScopes, this.getSilentPlannerRequest());
    return this.getTokenPromise(message);
  }

  /** Used across starting parent window msal session as well as getting tokens. */
  private static postRequestToParentOfIframe = (event: Bixby.Constants.MsalAppEvents, payload: IframeMsalPayload) => {
    const msg = {
      event,
      payload,
    };
    LoggerService.log('postRequestToParentOfIframe', msg);
    window.parent.postMessage(msg, '*');
  };

  /**
   * Gets a token silently, or falls back to interactive redirect.
   */
  private static async getTokenRedirect(
    silentRequest: SilentRequest,
    interactiveRequest: RedirectRequest
  ): Promise<string | null> {
    let result: string | null = null;
    try {
      const response = await MsalService.myMSALObj?.acquireTokenSilent(silentRequest);
      result = response?.accessToken ?? null;
    } catch (e: any) {
      // eslint-disable-next-line no-console
      console.log('silent token acquisition fails.');
      if (e instanceof InteractionRequiredAuthError) {
        // eslint-disable-next-line no-console
        console.log('acquiring token using redirect');
        // eslint-disable-next-line no-console
        MsalService.myMSALObj?.acquireTokenRedirect(interactiveRequest).catch(console.error);
      } else {
        // eslint-disable-next-line no-console
        console.error(e);
      }
    }

    return result;
  }

  /**
   * Calls getAllAccounts and determines the correct account to sign into, currently defaults to first account found in cache.
   * TODO: Add account chooser code
   *
   * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-common/docs/Accounts.md
   */
  private static determineAccount(): AccountInfo | null {
    // need to call getAccount here?

    let result: AccountInfo | null = null;
    const currentAccounts = MsalService.myMSALObj?.getAllAccounts() ?? null;
    if (currentAccounts === null) {
      // eslint-disable-next-line no-console
      console.log('No accounts detected');
    } else if (currentAccounts.length > 1) {
      // Add choose account code here
      // eslint-disable-next-line no-console
      console.log('Multiple accounts detected, need to add choose account code.');
      result = currentAccounts[0];
    } else if (currentAccounts.length === 1) {
      result = currentAccounts[0];
    }
    return result;
  }

  /**
   * Handles the response from a popup or redirect. If response is null, will check if we have any accounts and attempt to sign in.
   * @param response
   */
  private static handleResponse(response: AuthenticationResult | null) {
    if (response !== null && response.account !== null) {
      MsalService.account = response.account;
    } else {
      const account = MsalService.determineAccount();
      if (account !== null) {
        MsalService.account = account;
      }
    }
  }

  /** Enables resolution of async operation by parent window */
  private static getTokenPromise = async (message: IframeMsalPayload): Promise<string | null> => {
    LoggerService.log(`getTokenPromise with message`, message);
    if (window.parent === window) {
      throw new Error('No parent window available (not inside an iframe)');
    }
    const p = new Promise<string | null>((resolve, reject) => {
      const { pendingTokenRequestId: id = '' } = message.tokenRequest ?? {};
      const timer = setTimeout(() => {
        if (this.pendingTokenRequests.has(id)) {
          this.pendingTokenRequests.delete(id);
          reject(new Error('Token took too long to be received by the iframe msal app.'));
        }
      }, MsalService.PARENT_WINDOW_MSAL_TOKEN_TIMEOUT);
      const pendingTokenRequest: TokenAsyncRequest = {
        resolve,
        reject,
        timer,
      };
      this.pendingTokenRequests.set(id, pendingTokenRequest);
      this.postRequestToParentOfIframe(MsalAppEvents.MsalAppTokenRequest, message);
    });
    return p;
  };

  /** Central function to mediate payload creation for parent window msal handling via post message */
  private static getMsalInIframeRequest = (scopes: string[] = [], sr?: SilentRequest): IframeMsalPayload => {
    const request: IframeMsalPayload = {
      clientId: Bixby.Configuration.msalClientId,
      authority: Bixby.Configuration.msalAuthority,
      redirectRequest: {
        scopes: MsalService.loginScopes
      },
      tokenRequest: null,
      redirectUri: this.getCscpHostUri(),
      scopes,
    };
    if (sr) {
      request.tokenRequest = {
        pendingTokenRequestId: this.getNextTokenReqId(),
        silentRequest: sr,
      };
    }
    return request;
  };
  //#endregion
}
